/**
 * Confirmar a SIMULAÇÃO do Fee Fixo a partir de QUALQUER conversa.
 *
 * O cartão `fee-fixo/simulacao-card` pode chegar em três superfícies: a do Guia
 * (`view-fee-fixo-guia.vue`), a do drawer do Especialista e — desde a Home IA
 * (`/home-ia`) — a do chat do intelligence-v2. As duas primeiras têm o método
 * `confirmarSimulacao` (`views/guia/view-fee-fixo-guia-simulacao.js`); a
 * terceira só conhece o canal genérico `@respond`, que manda "Confirmo" ao
 * modelo e nada mais.
 *
 * ★ **Medido em 04/09/2026 na Home IA:** clicar em "Salvar cenário" mandava o
 * "Confirmo" genérico, e o modelo respondeu *"a confirmação acontece pelo
 * clique no cartão, na tela do Fee Lab — não por aqui no chat"*. Nada foi
 * gravado em `partners_fee_fixo_analise`, o cartão saiu da tela e a conversa
 * seguiu como se o cenário estivesse salvo. Este arquivo é o passo local que
 * faltava, publicado no índice de confirmadores para que a superfície não
 * precise conhecer o Fee Fixo.
 *
 * ★ **A ORDEM É O CONTRATO — a MESMA do Guia:** depositar → só com
 * `status === 200`, mandar a frase. Os `resultados` são calculados no
 * navegador (o motor do Fee Lab é um só) e o commit os exige; a confirmação
 * viaja como TEXTO porque o servidor a reconhece antes de o modelo vê-la, e
 * nenhuma frase carrega um objeto de 25 chaves. Frase sem depósito faz o
 * servidor recusar por um motivo que o consultor não tem como entender.
 * Contrato: `.specify/.../spec-007-simulacao-pelo-chat/contracts/
 * deposito-de-resultados.md`.
 *
 * ⚠️ **O que este arquivo NÃO replica do Guia**, de propósito:
 *
 * - a guarda de "trocou de conversa durante o depósito" — quem resolve o
 *   destino da frase é a superfície, que já a manda pela instância de `APIChat`
 *   dela; a guarda do Guia existe porque lá a troca de thread acontece dentro
 *   da mesma view;
 * - o `resetSimulacaoPendente` — aquele slice é do canal LEGADO das telas do
 *   Fee Fixo (`feeFixoIa`), e na Home IA quem desenha o cartão é o registry, a
 *   partir da parte do turno. Despachar aqui mexeria numa store que a conversa
 *   corrente não usa.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { registrarConfirmador } from '@modules/aawz-intelligence-v2/ui-registry/confirmadores'
import { apiFeeFixoSimulacaoDeposito } from '@modules/fee-fixo/api/apiFeeFixoSimulacaoDeposito'
import {
  mensagemDeConfirmacao,
  mensagemDeRecusa,
} from '@modules/fee-fixo/api/apiFeeFixoIa'
import { OPERACAO_CRIAR_SIMULACAO } from '@modules/fee-fixo/constants/escritas-ia'
import { CHAVES } from '@modules/fee-fixo/ia/registry'

/** As duas ações do contrato do canal genérico (`ui-components.md` §2). */
const ACAO_CONFIRMAR = 'confirmar'
const ACAO_CANCELAR = 'cancelar'

/**
 * O passo local da simulação.
 *
 * @param {Object}  ctx
 * @param {string}  ctx.acao   `confirmar` | `cancelar`.
 * @param {?Object} ctx.dados  O payload que o cartão emitiu —
 *                             `{previewId, resultados}` no confirmar.
 * @returns {Promise<?{enviar?: Object, erro?: Object}>} `null` devolve o
 *   cartão ao caminho genérico.
 */
export async function confirmarSimulacaoDoChat({ acao, dados } = {}) {
  if (acao === ACAO_CANCELAR) {
    // Nada foi gravado — o preview não criou linha alguma. O que a recusa
    // precisa é chegar ao MODELO: sem ela, ele reoferece no turno seguinte o
    // mesmo preview que o consultor acabou de descartar.
    const vozes = mensagemDeRecusa(OPERACAO_CRIAR_SIMULACAO)
    return vozes ? { enviar: vozes } : null
  }
  if (acao !== ACAO_CONFIRMAR) return null

  const previewId = (dados && dados.previewId) || ''
  const resultados = (dados && dados.resultados) || null
  const vozes = mensagemDeConfirmacao(OPERACAO_CRIAR_SIMULACAO, previewId)
  // Sem os três, o commit seria recusado no servidor. Declinar aqui devolve o
  // cartão ao caminho genérico, que é o comportamento de antes desta ligação —
  // nunca pior que ele.
  if (!vozes || !previewId || !resultados) return null

  const r = await apiFeeFixoSimulacaoDeposito.depositar({
    previewId: previewId,
    resultados: resultados,
  })
  // ★ Depósito que não confirmou NÃO manda a frase. A mensagem do servidor já
  // diz o que fazer (404 pede rodar de novo; 400 acusa números diferentes para
  // o mesmo id), e reescrevê-la trocaria uma instrução certa por uma genérica.
  if (!r || r.status !== 200) return { erro: r || {} }
  return { enviar: vozes }
}

/**
 * Publica o confirmador. Chamado no boot do módulo (`fee-fixo/index.js`), pelo
 * mesmo motivo do `registrar(registryFeeFixo)` ao lado: o cartão pode chegar em
 * QUALQUER conversa, e registrar numa view faria o mesmo clique gravar numa
 * tela e falhar na outra.
 */
export function registrarConfirmadorDeSimulacao() {
  return registrarConfirmador(CHAVES.SIMULACAO, confirmarSimulacaoDoChat)
}
